import dayjs from 'dayjs';
import { groupBy, orderBy, startCase, upperFirst } from 'lodash';

import type { SearchableSelectOption } from '@/components/SearchableSelect';
import type { DeviceTypeDTO } from '@/features/DeviceCreate/DeviceCreate.types';
import type {
  DetailedSourceDTO,
  DeviceDTO,
  DeviceModel,
  LimaSourcesResponseModal,
  SourceModel
} from '@/features/DevicesTable/DevicesTable.types';
import groupSourceStatusCodes from '@/features/DevicesTable/utils/LimaSourceAggregatedStatus.utils';
import type { BasicSourceTypeDTO } from '@/features/SourceCreate';
import SOURCE_TYPE from '@/features/SourceCreate/SourceCreate.constants';

import { DEVICE_STATUS, STATUS_LIST } from './Devices.constants';
import type { LimaSourceAggregatedStatusModel } from './SyncDevices/SyncDevices.types';

const toTitleCase = (word: string): string => {
  return word.substring(0, 1).toUpperCase() + word.substring(1).toLowerCase();
};

export const beautifyJson = (jsonInput: string) => {
  try {
    const parsedJson = JSON.parse(jsonInput);
    return JSON.stringify(parsedJson, null, 2); // Beautify
  } catch {
    return null;
  }
};

export const minifyJson = (jsonInput: string) => {
  try {
    const parsedJson = JSON.parse(jsonInput);
    return JSON.stringify(parsedJson); // Minify
  } catch {
    return null;
  }
};

// humanize time calculation
export const humanizeTime = (dateStr: string, withSuffix: boolean) => {
  if (!dateStr) return '';

  const inputLocal = dayjs.utc(dateStr).local(); // convert backend UTC → user's timezone
  const nowLocal = dayjs(); // user's current local time

  const diffMinutes = inputLocal.diff(nowLocal, 'minute', true);

  const humanized = dayjs.duration(diffMinutes, 'minutes').humanize(withSuffix);

  return toTitleCase(humanized);
};

// Calculating Status Based on status code value
export const calculateStatusFromStatusCode = (statusCode: string) => {
  let status = '';
  if (statusCode.startsWith('1801')) {
    status = DEVICE_STATUS.DEVICE_ACTIVATED;
  } else if (statusCode.startsWith('18')) {
    status = DEVICE_STATUS.NEW;
  } else if (statusCode.startsWith('19')) {
    status = DEVICE_STATUS.DISABLED;
  } else if (statusCode.startsWith('1')) {
    status = DEVICE_STATUS.UNKNOWN;
  } else if (statusCode.startsWith('2201')) {
    status = DEVICE_STATUS.CONFIG_SUCCESS;
  } else if (statusCode.startsWith('2001')) {
    status = DEVICE_STATUS.OPERATIONAL;
  } else if (statusCode.startsWith('2')) {
    status = DEVICE_STATUS.OPERATIONAL;
  } else if (statusCode.startsWith('3002')) {
    status = DEVICE_STATUS.DECOMMISSIONING;
  } else if (statusCode.startsWith('3003')) {
    status = DEVICE_STATUS.CATEGORY_FAILED;
  } else if (statusCode.startsWith('3004')) {
    status = DEVICE_STATUS.PENDING_RESTART;
  } else if (statusCode.startsWith('3')) {
    status = DEVICE_STATUS.WARNING;
  } else if (statusCode.startsWith('4201')) {
    status = DEVICE_STATUS.CONFIG_FAILED;
  } else if (statusCode.startsWith('4202')) {
    status = DEVICE_STATUS.DEPLOYMENT_FAILED;
  } else if (statusCode.startsWith('4203')) {
    status = DEVICE_STATUS.JOB_FAILED;
  } else if (statusCode.startsWith('4')) {
    status = DEVICE_STATUS.ERROR;
  } else {
    status = 'Unknown';
  }
  return status;
};

export const getStatusColor = (status: string) => {
  if (status === 'Active' || status === 'Operational') {
    return 'success';
  }
  if (status === 'Unknown') {
    return 'secondary';
  }

  if (status === 'New') {
    return 'secondary';
  }

  if (status === 'Disabled') {
    return 'secondary';
  }
  if (status === 'Warning') {
    return 'warning';
  }
  if (status === 'Error') {
    return 'error';
  }
  return 'error';
};

export const getLimaSourceStatusFilterOptions = (
  limaSources: LimaSourcesResponseModal[]
): SearchableSelectOption[] => {
  // Flatten sources to include nested data providers for accurate counting
  const flattenedSources = limaSources.flatMap((source) => {
    const sources = [source];
    if (source.data_providers?.length) {
      sources.push(...source.data_providers);
    }
    return sources;
  });

  const groupedLimaSourcesData = groupBy(flattenedSources, 'status');

  return STATUS_LIST.map((row) => ({
    label: row.label,
    value: row.value,
    count: groupedLimaSourcesData[row.label]?.length || 0,
    id: row.id
  }));
};

export const getLimaSourceFilterOptions = (
  sourceTypes: BasicSourceTypeDTO[],
  limaSources: LimaSourcesResponseModal[]
): SearchableSelectOption[] => {
  // Flatten sources to include nested data providers for accurate counting
  const flattenedSources = limaSources.flatMap((source) => {
    const sources = [source];
    if (source.data_providers?.length) {
      sources.push(...source.data_providers);
    }
    return sources;
  });

  const groupedDevicesData = groupBy(flattenedSources, 'source_type.type');

  // Extract unique source types from actual data
  // This ensures we show counts for all source types present, especially important for "All Organizations"
  const sourceTypesFromData = new Map<number, { type: string; id: number }>();
  flattenedSources.forEach((source) => {
    if (source.source_type?.id && source.source_type?.type) {
      sourceTypesFromData.set(source.source_type.id, {
        id: source.source_type.id,
        type: source.source_type.type
      });
    }
  });

  // Merge provided source types with those found in data
  const allSourceTypes = [...sourceTypes];
  sourceTypesFromData.forEach((sourceType) => {
    if (!allSourceTypes.find((st) => st.id === sourceType.id)) {
      allSourceTypes.push(sourceType as BasicSourceTypeDTO);
    }
  });

  // Include only top-level source types (parent_source_type_id === null),
  // excluding Microsoft data providers (O365, Microsoft Defender, and Microsoft Entra ID).

  return allSourceTypes
    .filter((sourceType) => sourceType.parent_source_type_id === null)
    .map((row: BasicSourceTypeDTO) => ({
      label: row.name?.trim().replace(/\s+/g, '') ?? '',
      value: row.id.toString() || '',
      count: groupedDevicesData[row.type?.trim() || '']?.length || 0,
      id: row.id || 0
    }));
};

export const getStatusFilterOptions = (
  devices: DeviceDTO[]
): SearchableSelectOption[] => {
  const groupedDevicesData = groupBy(devices, 'status');

  return STATUS_LIST.map((row) => ({
    label: row.label,
    value: row.value,
    count: groupedDevicesData[row.label]?.length || 0,
    id: row.id
  }));
};

export const getDeviceTypeFilterOptions = (
  deviceTypes: DeviceTypeDTO[],
  devices: DeviceDTO[]
): SearchableSelectOption[] => {
  const groupedDevicesData = groupBy(devices, 'device_type.type');
  return deviceTypes.map((row: DeviceTypeDTO) => ({
    label: row.type || '',
    value: row.id.toString() || '',
    count: groupedDevicesData[row?.type || '']?.length || 0,
    id: row.id || 0
  }));
};

export const getLogSourceFilterOptions = (
  devices: DeviceDTO[]
): SearchableSelectOption[] => {
  const sourceList: string[] = [];
  devices.forEach((dev: DeviceDTO) => {
    (
      dev as {
        sources?: Array<{
          connection_inputs?: { sources?: string[] };
        }>;
      }
    )?.sources?.[0]?.connection_inputs?.sources?.forEach((source: string) => {
      sourceList.push(source);
    });
  });

  const groupedOptions = groupBy(sourceList);

  return Object.keys(groupedOptions)
    .map((key) => ({
      label: startCase(key),
      value: key,
      count: groupedOptions[key]?.length || 0,
      id: key
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const parsePayload = (payloadBody: string) => {
  try {
    if (payloadBody.includes('~~~')) {
      const statusData = payloadBody
        .split('~~~')[0]
        .replace('\n', '')
        .replace('%%%', '');
      const statusJson = JSON.parse(statusData);
      return statusJson?.failed_categories || '';
    }
  } catch {
    return '';
  }
  return '';
};

const updateSourceCountStatus = (deviceRow: DeviceModel) => {
  let sourceCount = 0;
  let sourceStatus = '';

  const statusCounts = {
    Active: 0,
    Error: 0,
    Warning: 0,
    Unknown: 0
  };

  deviceRow.sources?.forEach((element) => {
    if (
      statusCounts[element.status as keyof typeof statusCounts] !== undefined
    ) {
      statusCounts[element.status as keyof typeof statusCounts] += 1;
    }
  });

  if (statusCounts.Error > 0) {
    sourceStatus = 'Error';
  } else if (statusCounts.Unknown > 0) {
    sourceStatus = 'Unknown';
  } else if (statusCounts.Active > 0) {
    sourceStatus = 'Active';
  } else if (statusCounts.Warning > 0) {
    sourceStatus = 'Warning';
  }
  sourceCount = deviceRow.sources?.length || -1;
  // For LogForwarders device type we need to show log sources count
  if (deviceRow.device_type.id === 2) {
    sourceCount =
      (
        deviceRow as {
          sources?: Array<{
            connection_inputs?: { sources?: string[] };
          }>;
        }
      )?.sources?.[0]?.connection_inputs?.sources?.length || 0;
  }

  // For Lima Source Aggregated Status
  if (deviceRow.device_type.id === 1) {
    sourceCount = 0;
    deviceRow?.lima_source_aggregated_status?.forEach((element) => {
      sourceCount += element.record_count || 0;
    });

    // Calculating lima device source count status ie highest order of all source status
    const aggSourceStatuses = orderBy(
      deviceRow?.lima_source_aggregated_status,
      'status_code',
      'desc'
    );
    const status_code = aggSourceStatuses[0]?.status_code || '';
    sourceStatus = calculateStatusFromStatusCode(status_code.toString() || '');
  }

  return { sourceCount, sourceStatus };
};

export const updateDeviceStatus = (deviceData: DeviceModel): DeviceModel => {
  const deviceRow = deviceData;
  if (deviceRow) {
    // Calculating Device Status Based on last seen value
    const lastSeenDisplay = humanizeTime(deviceRow.last_seen || '', true);

    // Check Sync is in progress
    // const isSyncInProgress = deviceData?.devices_tf_jobs !== null;

    // Handling multiple Status Codes
    let deviceStatus = deviceRow?.device_statuses || [];
    // Sorting by status code in descending order to get the highest order of status code
    deviceStatus = orderBy(deviceStatus, 'status_code', 'desc');
    const status_code = deviceStatus[0]?.status_code || '';
    const status = calculateStatusFromStatusCode(status_code.toString() || '');
    const statusUpdatedAtDisplay = humanizeTime(
      deviceStatus[0]?.status_updated_at || '',
      true
    );
    const status_label =
      deviceStatus[0]?.status_code_detail?.status_label || '';

    // Calculating Souce Count and Source Status
    const { sourceCount, sourceStatus } = updateSourceCountStatus(deviceRow);
    return {
      ...deviceRow,
      status,
      last_seen_display: lastSeenDisplay,
      status_label,
      status_updated_at_display: statusUpdatedAtDisplay,
      source_count: sourceCount,
      source_status: sourceStatus
      // isSyncInProgress
    };
  }
  return deviceRow;
};

export const updateLimaSourceAggregationStatus = (
  aggData: LimaSourceAggregatedStatusModel
): LimaSourceAggregatedStatusModel => {
  const aggRow = aggData;
  if (aggRow) {
    const lastSeenDisplay = humanizeTime(aggRow.last_seen || '', true);
    const status_code = aggRow?.status_code || '';
    const status = calculateStatusFromStatusCode(status_code.toString() || '');

    return {
      ...aggRow,
      status,
      last_seen_display: lastSeenDisplay
    };
  }
  return aggRow;
};

export const updateCloudSourceStatus = (
  subRow: LimaSourcesResponseModal
): LimaSourcesResponseModal => {
  if (!subRow) return subRow;

  const getPrefix = (sourceType: SOURCE_TYPE): string => {
    switch (sourceType) {
      case SOURCE_TYPE.AWS_CLOUDTRAIL:
        return 'Failed Regions: ';
      case SOURCE_TYPE.SALESFORCE:
        return 'Failed custom object(s) found';
      default:
        return 'Failed Categories: ';
    }
  };

  const failedCategoriesTooltipPrefix = getPrefix(
    subRow.source_type_id as SOURCE_TYPE
  );

  let category_failed_tooltip_text = null;
  if (subRow.source_type_id !== SOURCE_TYPE.SALESFORCE) {
    const failed_categories =
      subRow?.log_type_statuses
        ?.filter((log) => log.status.toLowerCase() === 'failed')
        .map((log) => log.log_type) || [];
    if (failed_categories.length > 0) {
      category_failed_tooltip_text =
        failedCategoriesTooltipPrefix + failed_categories.join(', ');
    }
  } else if (subRow.source_type_id === SOURCE_TYPE.SALESFORCE) {
    const failed_categories =
      subRow?.log_type_statuses
        ?.filter((log) => !log.log_type.startsWith('Salesforce_'))
        .map((log) => log.log_type) || [];
    if (failed_categories.length > 0) {
      category_failed_tooltip_text = failedCategoriesTooltipPrefix;
    }
  }

  const lastSeenDisplay = humanizeTime(subRow.last_seen || '', true);

  const sourceStatuses = orderBy(
    subRow?.source_statuses || [],
    'status_code',
    'desc'
  );
  const status_code = sourceStatuses[0]?.status_code || '';

  const status = calculateStatusFromStatusCode(status_code.toString());
  const status_label =
    sourceStatuses[0]?.status_code_detail?.status_label || '';

  const getLimaDeviceName = (row: LimaSourcesResponseModal) => {
    if ((row.lima_devices.display_name?.length ?? 0) > 0) {
      return row.lima_devices.display_name;
    }
    return row.lima_devices.host_name;
  };

  const getDisplaySourceName = (
    source_type_id: number,
    source_type: string,
    source_name: string,
    site_name?: string
  ): string => {
    // Sanitize and normalize inputs
    const sourceTypeName = source_type?.trim().replace(/\s+/g, '') ?? '';
    const cleanSiteName = site_name?.trim().replace(/\s+/g, '') ?? '';
    const cleanSourceName = source_name?.trim() ?? '';

    let apiEndpoint = '';

    // Identify API endpoint name for Microsoft Entra ID (id = 36)
    if (source_type_id === SOURCE_TYPE.MICROSOFT_ENTRA_ID) {
      const upperName = cleanSourceName.toUpperCase();
      if (upperName.includes('GRAPH')) {
        apiEndpoint = 'Graph';
      } else if (upperName.includes('O365')) {
        apiEndpoint = 'O365';
      }
    }

    // Build final name
    const parts = [sourceTypeName, apiEndpoint, cleanSiteName].filter(Boolean);
    return parts.join('-');
  };

  return {
    ...subRow,
    display_source_name: getDisplaySourceName(
      subRow.source_type_id,
      subRow.source_type.name,
      subRow.source_name,
      subRow?.site_name || ''
    ),
    org_label: subRow?.organization?.label || '',
    status,
    active_state: subRow?.active ? 'Active' : 'Inactive',
    is_selected: false,
    status_label,
    last_seen_display: lastSeenDisplay,
    category_failed_tooltip_text,
    device_name: getLimaDeviceName(subRow) || ''
  };
};

const updateBasicSourceStatus = (subrow: DetailedSourceDTO): SourceModel => {
  const subRow = subrow as SourceModel;
  if (subRow) {
    // Calculating Source Status Based on last seen value
    const lastSeenDisplay = humanizeTime(subRow.last_seen || '', true);
    // Handling multiple Status Codes
    let sourceStatuses = subRow?.source_statuses || [];
    // Sorting by status code in descending order to get the highest order of status code
    sourceStatuses = orderBy(sourceStatuses, 'status_code', 'desc');
    const status_code = sourceStatuses[0]?.status_code || '';
    const status = calculateStatusFromStatusCode(status_code.toString() || '');
    const statusUpdatedAtDisplay = humanizeTime(
      sourceStatuses[0]?.status_updated_at || '',
      true
    );
    const status_label =
      sourceStatuses[0]?.status_code_detail?.status_label || '';

    // When status code is 3100 replace status label place holder with failed categories from payload
    // if (
    //   subRow.status_code === STATUS_CODES.STATUS_LABEL_WITH_PLACE_HOLDER &&
    //   subRow.status_code_detail?.status_label &&
    //   subRow.payload
    // ) {
    //   const payload: { body?: string } = subRow?.payload;
    //   const payloadBody: string = payload.body || '';
    //   subRow.status_code_detail.status_label =
    //     subRow?.status_code_detail?.status_label?.replace(
    //       '({{source_type}})',
    //       parsePayload(payloadBody) || ''
    //     );
    // }

    return {
      ...subRow,
      status,
      last_seen_display: lastSeenDisplay,
      status_label,
      status_updated_at_display: statusUpdatedAtDisplay
    };
  }
  return subRow;
};

export const updateDeviceDTO = (devices: DeviceModel[]): DeviceModel[] => {
  // update all sources with last seen and status
  const updatedSourcesByLastSeen = devices.map((devrow: DeviceModel) => {
    const deviceRow = devrow;
    if (deviceRow?.sources) {
      deviceRow.sources = deviceRow.sources?.map(
        (subrow: DetailedSourceDTO) => {
          return updateBasicSourceStatus(subrow);
        }
      );
    }

    // update status for lima source aggregated data
    if (deviceRow?.lima_source_aggregated_status) {
      deviceRow.lima_source_aggregated_status_display = groupSourceStatusCodes(
        deviceRow?.lima_source_aggregated_status
      );
    }

    return deviceRow;
  });
  // update all devices with last seen, status ,source_count and source_status
  const updatedDevices = updatedSourcesByLastSeen.map((devrow: DeviceModel) => {
    return updateDeviceStatus(devrow);
  });
  return updatedDevices;
};

export const formatSourceName = (sourceName: string) => {
  // Split the string by underscores and capitalize each part
  if (sourceName.includes('_')) {
    const parts = sourceName.split('_');
    return parts.map((part) => upperFirst(part.toLowerCase())).join(' ');
  }

  // Check if the string matches the pattern for alphanumeric combinations like "O365"
  if (/^[a-zA-Z]+\d+$/.test(sourceName)) {
    // Capitalize the first letter without adding spaces
    return upperFirst(sourceName.toLowerCase());
  }
  // Default Capitalize the first letter of each word
  return startCase(sourceName);
};
